export abstract class Authentication
{
    private refreshPromise: Promise<string> | null = null;
    private lastProactiveRefreshAttempt = 0;

    private readonly proactiveRefreshThresholdSeconds = 120;
    private readonly proactiveRefreshCooldownMs = 30_000;
    private readonly refreshTimeoutMs = 10_000;

    // Operations a product-specific subclass must supply. The internal ones are
    // protected; setToken / clearStorage / logOut are part of the public API so
    // must stay public.
    protected abstract getStoredToken(): string;
    protected abstract getProviderType(): string;
    protected abstract refreshAadToken(): Promise<string>;
    protected abstract refreshOktaToken(): Promise<string>;
    protected abstract refreshAuth0Token(): Promise<string>;
    public abstract setToken(value: string): void;
    public abstract clearStorage(): Promise<void>;
    public abstract logOut(reason?: LogOutReason): Promise<void>;

    public getToken(): string
    {
        const token = this.getStoredToken();
        if (!token)
        {
            return "";
        }

        if (!this.isTokenValid(0, token))
        {
            this.setToken("");
            return "";
        }

        if (this.isTokenExpiringSoon(this.proactiveRefreshThresholdSeconds, token))
        {
            this.tryProactiveRefresh();
        }

        return token;
    }

    public isTokenValid(clockSkewSeconds = 0, token = this.getStoredToken()): boolean
    {
        if (!token)
        {
            return false;
        }

        const expiresOn = this.getTokenExpiry(token);
        if (expiresOn === null)
        {
            // If this is not a JWT, retain existing behavior and treat it as valid.
            return true;
        }

        const now = Math.floor(Date.now() / 1000);
        return expiresOn > (now + clockSkewSeconds);
    }

    public isTokenExpiringSoon(thresholdSeconds = this.proactiveRefreshThresholdSeconds, token = this.getStoredToken()): boolean
    {
        if (!token)
        {
            return false;
        }

        const expiresOn = this.getTokenExpiry(token);
        if (expiresOn === null)
        {
            return false;
        }

        const now = Math.floor(Date.now() / 1000);
        return expiresOn <= (now + thresholdSeconds);
    }

    private getTokenExpiry(token: string): number | null
    {
        try
        {
            const jwtParts = token.split(".");
            if (jwtParts.length < 2)
            {
                return null;
            }

            const payload = jwtParts[1]
                .replace(/-/g, "+")
                .replace(/_/g, "/");

            const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
            const decodedPayload = atob(paddedPayload);
            const parsedPayload = JSON.parse(decodedPayload) as { exp?: number };

            if (typeof parsedPayload.exp !== "number")
            {
                return null;
            }

            return parsedPayload.exp;
        }
        catch
        {
            return null;
        }
    }

    public async logOutIfTokenInvalid(): Promise<void>
    {
        const token = this.getStoredToken();
        if (!token)
        {
            return;
        }

        if (!this.isTokenValid())
        {
            await this.logOut(LogOutReason.SessionExpired);
            return;
        }

        if (this.isTokenExpiringSoon())
        {
            try
            {
                await this.refreshToken();
            }
            catch
            {
                await this.logOut(LogOutReason.SessionExpired);
            }
        }
    }

    public async refreshToken(timeoutMs = this.refreshTimeoutMs): Promise<string>
    {
        if (this.refreshPromise)
        {
            return this.refreshPromise;
        }

        this.refreshPromise = this
            .withTimeout(signal => this.runRefreshToken(signal), timeoutMs)
            .finally(() =>
            {
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    private withTimeout<T>(taskFactory: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T>
    {
        const timeoutController = new AbortController();
        const timeoutId = window.setTimeout(() => timeoutController.abort(), timeoutMs);

        const timeoutTask = new Promise<T>((_, reject) =>
        {
            timeoutController.signal.addEventListener(
                "abort",
                () => reject(new Error("Token refresh timed out.")),
                { once: true }
            );
        });

        const task = taskFactory(timeoutController.signal);

        return Promise.race([task, timeoutTask]).finally(() =>
        {
            window.clearTimeout(timeoutId);
        });
    }

    private async runRefreshToken(signal: AbortSignal): Promise<string>
    {
        const securityProviderTypeId = this.getProviderType();
        let token = '';

        switch (securityProviderTypeId)
        {
            case 'Native':
                throw new Error('Native authentication does not support refreshing tokens.');
            case 'AAD':
                token = await this.refreshAadToken();
                break;
            case 'Okta':
                token = await this.refreshOktaToken();
                break;
            case 'Auth0':
                token = await this.refreshAuth0Token();
                break;
            default:
                throw new Error('Not supported.');
        }

        if (signal.aborted)
        {
            throw new Error('Token refresh timed out.');
        }

        this.setToken(token);
        return token;
    }

    private tryProactiveRefresh(): void
    {
        const now = Date.now();
        const canAttemptRefresh = (now - this.lastProactiveRefreshAttempt) > this.proactiveRefreshCooldownMs;
        if (!canAttemptRefresh)
        {
            return;
        }

        this.lastProactiveRefreshAttempt = now;
        this.refreshToken().catch(() => { return; });
    }
}

export enum LogOutReason
{
    NotSet = 0,
    SessionExpired,
    AccountIncomplete,
    LoginFailed,
    VersionChanged,
}
